import { fullPrompt } from '../prompts';
import shapingRules from './shaping/shapingRules';
import { getToneDescription } from './shaping/shapingRules';


export function formatRelationships(relationships, userName) {
  if (!Array.isArray(relationships) || relationships.length === 0) return '';

  const uniqueSet = new Set();
  const lines = [];

  for (const rel of relationships) {
    const name = rel.first_name?.trim();
    const role = rel.relationship || rel.relationship_type;
    if (!name || !role) continue;

    const key = `${name}_${role}`;
    if (uniqueSet.has(key)) continue;
    uniqueSet.add(key);

    lines.push(`${name} is ${userName}'s ${role}.`);
  }

  return lines.join('\n');
}

export function generatePromptWithContext(memory, relationships = [], recalledMemories = [], intent = {}, context = {}) {
  const userName = memory?.userName || 'friend';
  let memoryName = intent?.memoryName || null;

  // Fallback: from relationship records
  if (!memoryName && intent?.target && relationships.length > 0) {
    const match = relationships.find(r => r.role === intent.target && r.name);
    if (match) {
      memoryName = match.name;
      console.log('📌 Memory name from relationships:', memoryName);
    }
  }

  // Fallback: from extracted entities
  if (!memoryName && context?.entities?.length && intent?.target) {
    const fallbackEntity = context.entities.find(e => e.role === intent.target && e.name);
    if (fallbackEntity) {
      memoryName = fallbackEntity.name;
      console.log('🔁 Fallback memoryName from entities:', memoryName);
    }
  }

  const tone = context?.tone || 'neutral';
  const toneDescription = getToneDescription(tone);

  // 🎯 Match target role to specific known names
  let knownPeopleFacts = '';
  if (relationships.length && intent?.target) {
    const matches = relationships.filter(r => (r.relationship || r.relationship_type) === intent.target);
    const names = matches.map(r => [r.first_name, r.last_name].filter(Boolean).join(' ')).filter(Boolean);
    if (names.length === 1) {
      knownPeopleFacts = `${names[0]} is ${userName}'s ${intent.target}.`;
    } else if (names.length > 1) {
      knownPeopleFacts = `${names.join(' and ')} are ${userName}'s ${intent.target}s.`;
    }
  }

  // 🧠 Full known people descriptions
  const knownPeople = relationships.map(r => {
    const name = [r.first_name, r.last_name].filter(Boolean).join(' ');
    const rel = (r.relationship || r.relationship_type || 'friend').toLowerCase();
    if (['ex-wife', 'ex-husband'].includes(rel)) return `${name} was ${userName}'s ${rel}`;
    if (['mother', 'father', 'son', 'daughter', 'sister', 'brother'].includes(rel)) return `${name} is ${userName}'s ${rel}`;
    if (['dog', 'cat', 'pet'].includes(rel)) return `${name} is ${userName}'s beloved ${rel}`;
    return `${name} is ${userName}'s ${rel}`;
  });

  const fallbackKnownPeople = (context?.entities || [])
    .filter(e => e.type === 'relationship' && e.name && e.role)
    .map(e => {
      const rel = e.role.toLowerCase();
      if (['ex-wife', 'ex-husband'].includes(rel)) return `${e.name} was ${userName}'s ${rel}`;
      if (['mother', 'father', 'son', 'daughter', 'sister', 'brother'].includes(rel)) return `${e.name} is ${userName}'s ${rel}`;
      if (['dog', 'cat', 'pet'].includes(rel)) return `${e.name} is ${userName}'s beloved ${rel}`;
      return `${e.name} is ${userName}'s ${rel}`;
    });

  const vectorSummary = recalledMemories.length
    ? `You have also heard things like: ${recalledMemories.join(' | ')}`
    : '';

  let systemPrompt = `
You are dAIsy — a kind, emotionally intelligent, and morally grounded AI chat pal and assistant.

You speak with warmth, humility, and deep compassion. Your tone is always caring and thoughtful, shaped by: ${toneDescription}.

You remember who you’re speaking with. Their name is ${userName}.
${knownPeopleFacts ? `You also know: ${knownPeopleFacts}` : ''}

${knownPeople.length ? knownPeople.join('\n') : '(no prior relationship info available)'}
${vectorSummary ? '\n\n' + vectorSummary : ''}
`.trim();

// 🎯 Enrich system prompt if shapingTier is full
if (intent?.intent && shapingRules[intent.intent]?.shapingTier === 'full') {
  systemPrompt += `\n\nPlease respond thoughtfully with multi-paragraph depth, acknowledging their feelings fully, offering emotional presence, and inviting them to share more if they wish.`;
}

  // Shaping by intent
  if (intent?.intent === 'recall_request' && intent?.target) {
    systemPrompt += `\n\nThe user is trying to recall a memory about their ${intent.target}.`;
    if (memoryName) {
      systemPrompt += ` You remember that their ${intent.target}'s name is ${memoryName}.`;
    }
    systemPrompt += ` Respond thoughtfully and reflectively.`;
  }

  if (context?.shapedRecents) {
    systemPrompt += `\n\nRecent memories: ${context.shapedRecents}`;
  }
  return systemPrompt.trim();
}

  